import { useMutation } from "@apollo/client/react"
import LoadingButton from "@mui/lab/LoadingButton"
import { Dialog, IconButton, Alert, Button } from "@mui/material"
import { Formik, FormikErrors } from "formik"
import { Check, X } from "lucide-mui"
import React, { useState, useEffect } from "react"
import * as Yup from "yup"

import PeopleAmountInput from "@/components/people_amount_input"
import {
  STATUS_PENDING,
  STATUS_CONFIRMED,
  STATUS_WALK_IN,
  RESERVATION_COLORS,
  type ReservationStatus,
} from "@/constants/reservations"
import { useVenue } from "@/contexts/venue"
import { UPDATE_RESERVATION_REFETCH_QUERIES } from "@/graphql/dependencies"
import { CreateReservationDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import { formatTableName } from "@/shared/tables"
import { useStore } from "@/store"

import CustomerAutocomplete from "../create_form/customer"
import StatusField from "../shared/status_field"
import TimeRangeSelector from "../shared/time_range_selector"

const normalizePeopleAmount = (value: unknown): number | null => {
  if (value == null) return null
  if (typeof value === "string" && value.trim() === "") return null
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

interface CustomerFormData {
  name: string
  email: string
  phone: string
}

interface QuickCreateReservationFormValues {
  tableId: string
  tableName: string
  startTime: string
  endTime: string
  peopleAmount: string | number | null
  status: string
  customer: CustomerFormData
}

type ReservationType = "reservation" | "walk_in"

const CREATE_STATUSES: ReservationStatus[] = [STATUS_PENDING, STATUS_CONFIRMED]

const STYLES = {
  dialog: {
    paper: "w-[340px] max-w-[340px]",
  },
  header: {
    base: "px-4 py-2 text-white text-sm flex flex-row justify-between items-center",
    title: "font-medium",
  },
  content: {
    container: "p-4 space-y-3",
  },
  footer: {
    container: "border-t border-gray-200 p-4 flex justify-between items-center",
  },
}

const QuickCreateForm: React.FC = () => {
  const { venue } = useVenue()
  const {
    showQuickCreateReservationDialog,
    setShowQuickCreateReservationDialog,
    setShowCreateReservationDialog,
    currentDate,
    quickCreateReservationPrefilledValues,
  } = useStore()

  const reservationType: ReservationType = quickCreateReservationPrefilledValues?.reservationType || "reservation"

  const [createReservation, { loading: creating }] = useMutation(CreateReservationDocument, {
    refetchQueries: UPDATE_RESERVATION_REFETCH_QUERIES,
  })
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean | null>(null)

  const handleClose = () => {
    setShowQuickCreateReservationDialog(false)
    setError(null)
    setSuccess(null)
  }

  const handleOpenFullModal = () => {
    setShowQuickCreateReservationDialog(false)
    setShowCreateReservationDialog(true, quickCreateReservationPrefilledValues || undefined)
  }

  const validationSchema = Yup.object({
    tableId: Yup.string().required(i18n.t("app.forms.create_reservation.errors.table_required")),
    startTime: Yup.string().required(),
    endTime: Yup.string().required(),
    peopleAmount: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .min(1, i18n.t("app.forms.create_reservation.errors.people_amount_min")),
    status: Yup.string().required(i18n.t("app.forms.create_reservation.errors.status_required")),
    customer: Yup.object({
      name: Yup.string(),
      email: Yup.string().email(i18n.t("app.forms.create_reservation.errors.customer_email_invalid")),
      phone: Yup.string(),
    }),
  })

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => {
        setSuccess(null)
        handleClose()
      }, 1500)
      return () => clearTimeout(timeout)
    }
  }, [success])

  const getInitialValues = (): QuickCreateReservationFormValues => {
    const table = quickCreateReservationPrefilledValues?.tableId
      ? venue.tables.find((t: any) => t.id === quickCreateReservationPrefilledValues.tableId)
      : null

    return {
      tableId: quickCreateReservationPrefilledValues?.tableId || "",
      tableName: table ? formatTableName(table) || "" : "",
      startTime: quickCreateReservationPrefilledValues?.startTime || "",
      endTime: quickCreateReservationPrefilledValues?.endTime || "",
      peopleAmount: "",
      status: reservationType === "walk_in" ? STATUS_WALK_IN : STATUS_CONFIRMED,
      customer: {
        name: "",
        email: "",
        phone: "",
      },
    }
  }

  const onSubmit = async (values: QuickCreateReservationFormValues) => {
    setError(null)

    const peopleAmountValue = normalizePeopleAmount(values.peopleAmount)

    let result
    try {
      result = await createReservation({
        variables: {
          peopleAmount: peopleAmountValue,
          tableSlots: [
            {
              tableId: values.tableId,
              startTime: values.startTime,
              endTime: values.endTime,
            },
          ],
          venueId: venue.id,
          customer: values.customer,
          status: values.status,
          notes: [],
        },
      })
    } catch (e) {
      setError(i18n.t("app.reservation_preview.create_error"))
      console.error("Error creating reservation", e)
      return
    }

    const resultSuccess = result?.data?.createReservation?.success

    if (resultSuccess) {
      setSuccess(true)
    } else {
      setError(i18n.t("app.reservation_preview.create_error"))
      console.error("Error creating reservation", result)
    }
  }

  const headerColor =
    reservationType === "walk_in" ? RESERVATION_COLORS[STATUS_WALK_IN].main : RESERVATION_COLORS[STATUS_CONFIRMED].main

  const title =
    reservationType === "walk_in"
      ? i18n.t("app.quick_create_reservation.title_walk_in")
      : i18n.t("app.quick_create_reservation.title_reservation")

  return (
    <Dialog
      open={showQuickCreateReservationDialog}
      onClose={handleClose}
      PaperProps={{
        className: STYLES.dialog.paper,
      }}
      data-testid="panel-reservations-quick-create-form"
    >
      <Formik<QuickCreateReservationFormValues>
        initialValues={getInitialValues()}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => {
          const customerTouched = formik.touched.customer
          const customerErrors = formik.errors.customer as FormikErrors<CustomerFormData> | string | undefined

          return (
            <form onSubmit={formik.handleSubmit} className="flex flex-col">
              <div className={STYLES.header.base} style={{ backgroundColor: headerColor }}>
                <span className={STYLES.header.title}>{title}</span>
                <IconButton
                  onClick={handleClose}
                  color="inherit"
                  size="small"
                  data-testid="panel-reservations-quick-create-form-close-btn"
                >
                  <X className="text-lg" />
                </IconButton>
              </div>

              {!!error && (
                <Alert severity="error" className="m-2">
                  {error}
                </Alert>
              )}

              <div className={STYLES.content.container}>
                <CustomerAutocomplete
                  onChange={(data) => {
                    void formik.setFieldValue("customer", {
                      name: data.name,
                      email: data.email,
                      phone: data.phone,
                    })
                  }}
                  touched={
                    customerTouched && typeof customerTouched === "object"
                      ? {
                          name: customerTouched.name,
                          email: customerTouched.email,
                          phone: customerTouched.phone,
                        }
                      : {}
                  }
                  errors={
                    customerErrors && typeof customerErrors === "object"
                      ? {
                          name: customerErrors.name,
                          email: customerErrors.email,
                          phone: customerErrors.phone,
                        }
                      : {}
                  }
                />

                <PeopleAmountInput name="peopleAmount" label={i18n.t("app.reservation.labels.people_amount")} />

                <TimeRangeSelector venue={venue} date={currentDate} />

                {reservationType === "reservation" && <StatusField statuses={CREATE_STATUSES} />}
              </div>

              <div className={STYLES.footer.container}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleOpenFullModal}
                  className="text-gray-600"
                  data-testid="panel-reservations-quick-create-form-more-btn"
                >
                  {i18n.t("app.quick_create_reservation.more_options")}
                </Button>
                <LoadingButton
                  color={success ? "success" : "primary"}
                  loadingPosition="center"
                  loading={creating}
                  type="submit"
                  variant="contained"
                  data-testid="panel-reservations-quick-create-form-submit-btn"
                >
                  {success ? <Check /> : i18n.t("app.actions.save")}
                </LoadingButton>
              </div>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export default QuickCreateForm
